
import { useState, useEffect, useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useReferentBrandingColors } from '@/hooks/useReferentBrandingColors';


interface ReferentConfirmationData {
  name: string;
  position: string;
  stillWorksAtCompany: boolean;
  relationTypeConfirmed: string;
}

interface BrandingSettings {
  enabled: boolean;
  logo_url?: string;
  primary_color?: string;
}

interface ContextualInfoStepProps {
  referent: any;
  campaign: any;
  updateFormData: (data: any) => void;
  formData?: any;
  branding?: BrandingSettings | null;
}

export const ContextualInfoStep = ({ referent, campaign, updateFormData, formData, branding }: ContextualInfoStepProps) => {
  const colors = useReferentBrandingColors(branding);
  const [localFormData, setLocalFormData] = useState<ReferentConfirmationData>({
    name: formData?.referentName || referent?.name || '',
    position: formData?.referentPosition || referent?.position || '',
    stillWorksAtCompany: formData?.stillWorksAtCompany ?? true,
    relationTypeConfirmed: formData?.relationTypeConfirmed || '',
  });

  // Pré-remplir le type de relation avec celui attendu par le recruteur
  useEffect(() => {
    if (referent?.relation_type && !localFormData.relationTypeConfirmed) {
      setLocalFormData(prev => ({
        ...prev,
        relationTypeConfirmed: referent.relation_type
      }));
    }
  }, [referent, localFormData.relationTypeConfirmed]);

  // Mettre à jour formData quand les données locales changent
  useEffect(() => {
    updateFormData({
      relationTypeConfirmed: localFormData.relationTypeConfirmed,
      referentName: localFormData.name,
      referentPosition: localFormData.position,
      stillWorksAtCompany: localFormData.stillWorksAtCompany
    });
  }, [localFormData, updateFormData]);

  const relationTypes = [
    { value: 'manager', label: 'Manager direct' },
    { value: 'collègue', label: 'Collègue de même niveau' },
    { value: 'client', label: 'Client' },
    { value: 'autre', label: 'Autre' },
  ];

  const getRelationTypeLabel = (value: string) => {
    const type = relationTypes.find(t => t.value === value);
    return type ? type.label : value;
  };

  // Fonction pour obtenir l'entreprise spécifique du référent (mémorisée pour éviter les recalculs)
  const referentCompany = useMemo(() => {
    // Debug: Log uniquement en développement
    if (process.env.NODE_ENV === 'development') {
      console.warn('🔍 ContextualInfoStep - Référent:', { name: referent?.name, experience_id: referent?.experience_id });
      console.warn('🔍 DEBUG - Toutes les clés du référent:', Object.keys(referent || {}));
    }

    // Récupérer les expériences du candidat depuis les données AI
    const experiences = campaign?.ai_extracted_data?.experiences;

    if (!experiences || experiences.length === 0) {
      return 'Non spécifiée';
    }

    // Stratégie 1: Utiliser experience_id directement (solution robuste)
    if (referent?.experience_id) {
      const experience = experiences.find((exp: any) => exp.id === referent.experience_id);
      if (experience?.company) {
        console.warn('✅ Correspondance directe via experience_id:', experience.company);
        return experience.company;
      }
    }

    // Stratégie 2: Essayer de faire correspondre par nom/email du référent avec les recommendedReferents
    if (campaign?.ai_extracted_data?.recommendedReferents && referent?.name) {
      // Chercher un recommendedReferent qui pourrait correspondre à ce référent
      // (basé sur une correspondance approximative du nom ou du rôle)
      const matchingRecommended = campaign.ai_extracted_data.recommendedReferents.find((rec: any) => {
        if (!rec.experienceId) return false;

        // Vérifier si le rôle suggéré correspond au relation_type du référent
        const roleMatch = (rec.suggestedRole === 'manager direct' && referent.relation_type === 'manager') ||
          (rec.suggestedRole === 'collègue' && referent.relation_type === 'collègue');

        return roleMatch;
      });

      if (matchingRecommended?.experienceId) {
        const experience = experiences.find((exp: any) => exp.id === matchingRecommended.experienceId);
        if (experience?.company) {
          console.warn('✅ Correspondance par rôle:', experience.company);
          return experience.company;
        }
      }
    }

    // Stratégie 2: Utiliser la première expérience recommandée (la plus récente/prioritaire)
    if (campaign?.ai_extracted_data?.recommendedReferents?.length > 0) {
      const firstRecommended = campaign.ai_extracted_data.recommendedReferents[0];
      if (firstRecommended?.experienceId) {
        const experience = experiences.find((exp: any) => exp.id === firstRecommended.experienceId);
        if (experience?.company) {
          return experience.company;
        }
      }
    }

    // Stratégie 3: Essayer de faire correspondre par nom/position du référent
    if (referent?.name && referent?.position) {
      console.warn('🔍 Tentative correspondance par nom/position:', referent.name, referent.position);

      // Chercher une expérience qui pourrait correspondre au référent
      const matchingExperience = experiences.find((exp: any) => {
        // Vérifier si le poste du référent correspond à une expérience
        const positionMatch = exp.position && referent.position &&
          (exp.position.toLowerCase().includes(referent.position.toLowerCase()) ||
            referent.position.toLowerCase().includes(exp.position.toLowerCase()));

        console.warn(`🔍 Comparaison avec exp ${exp.id}:`, {
          expPosition: exp.position,
          refPosition: referent.position,
          positionMatch
        });

        return positionMatch;
      });

      if (matchingExperience?.company) {
        console.warn('✅ Entreprise trouvée par correspondance position:', matchingExperience.company);
        return matchingExperience.company;
      }
    }

    // Stratégie 4: Fallback vers l'entreprise la plus récente (première dans la liste)
    console.warn('⚠️ Utilisation fallback première expérience:', experiences[0]?.company);
    return experiences[0]?.company || 'Non spécifiée';
  }, [campaign, referent]); // Dépendances stables

  return (
    <div className="space-y-6 font-['Bricolage_Grotesque']">
      {/* Sous-titre spécifique à cette étape */}
      <div className="text-center mb-6">
        <p className="text-sm font-normal font-['Bricolage_Grotesque'] text-neutral-400 leading-none">
          Veuillez confirmer votre relation professionnelle avec le candidat.
        </p>
      </div>

      {/* Section d'information avec bordure gauche */}
      {referent?.relation_type && (
        <div className="p-2.5 bg-gray-50 border-l-2 border-teal-400 flex flex-col justify-center items-start gap-1">
          <div className="text-gray-800 text-base font-medium font-['Bricolage_Grotesque'] leading-tight">
            Type de relation attendu : {getRelationTypeLabel(referent.relation_type)}
          </div>
          <div className="text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque'] leading-tight">
            Le demandeur a expressément demandé une référence de « {getRelationTypeLabel(referent.relation_type).toLowerCase()} ».
            Veuillez confirmer que ce lien de parenté correspond à votre situation avec le candidat.
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Champs d'information */}
        <div className="space-y-4">
          {/* Nom et prénom (lecture seule) */}
          <div className="flex flex-col justify-start items-start gap-1.5">
            <div className="text-center justify-start text-neutral-400 text-base font-medium font-['Bricolage_Grotesque'] leading-tight">Nom et prénom</div>
            <div className="pl-2.5 border-l-2 border-neutral-400/20 flex flex-col justify-start items-start gap-1">
              <div className="text-gray-800 text-lg font-medium font-['Bricolage_Grotesque'] leading-snug p-0 w-full">
                {localFormData.name || 'Non renseigné'}
              </div>
              <div className="text-center justify-start text-gray-800 text-base font-light font-['Bricolage_Grotesque'] leading-tight">
                Informations fournies par le candidat (non modifiables)
              </div>
            </div>
          </div>

          {/* Position (lecture seule) */}
          <div className="flex flex-col justify-start items-start gap-1.5">
            <div className="text-center justify-start text-neutral-400 text-base font-medium font-['Bricolage_Grotesque'] leading-tight">Position</div>
            <div className="pl-2.5 border-l-2 border-neutral-400/20 flex flex-col justify-start items-start gap-1">
              <div className="text-gray-800 text-lg font-medium font-['Bricolage_Grotesque'] leading-snug p-0 w-full">
                {localFormData.position || 'Non renseigné'}
              </div>
              <div className="text-center justify-start text-gray-800 text-base font-light font-['Bricolage_Grotesque'] leading-tight">
                Informations fournies par le candidat (non modifiables)
              </div>
            </div>
          </div>

          {/* Entreprise */}
          <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
            <div className="text-center justify-start text-neutral-400 text-base font-medium font-['Bricolage_Grotesque'] leading-tight">Entreprise</div>
            <div className="self-stretch pl-2.5 border-l-2 border-neutral-400/20 flex flex-col justify-start items-start gap-1">
              <div className="text-center justify-start text-gray-800 text-lg font-medium font-['Bricolage_Grotesque'] leading-snug">{referentCompany}</div>
              <div className="self-stretch justify-start text-gray-800 text-base font-light font-['Bricolage_Grotesque'] leading-tight">Entreprise au sein de laquelle vous avez collaboré avec le candidat</div>
            </div>
          </div>
        </div>

        {/* Selects */}
        <div className="space-y-3">
          {/* Question sur l'entreprise actuelle */}
          <div className="space-y-2">
            <label className="text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
              Travaillez-vous toujours dans l'entreprise mentionnée par le candidat ?
            </label>
            <Select
              value={localFormData.stillWorksAtCompany.toString()}
              onValueChange={(value) => setLocalFormData(prev => ({ ...prev, stillWorksAtCompany: value === 'true' }))}
            >
              <SelectTrigger className="w-full px-3 py-3.5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20">
                <SelectValue placeholder="Sélectionnez une réponse" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Oui, je travaille toujours dans cette entreprise</SelectItem>
                <SelectItem value="false">Non, je ne travaille plus dans cette entreprise</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Type de relation professionnelle */}
          <div className="space-y-2">
            <label className="text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
              Type de relation professionnelle
            </label>
            <Select
              value={localFormData.relationTypeConfirmed}
              onValueChange={(value) => setLocalFormData(prev => ({ ...prev, relationTypeConfirmed: value }))}
            >
              <SelectTrigger className="w-full px-3 py-3.5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20">
                <SelectValue placeholder="Confirmez votre relation avec le candidat" />
              </SelectTrigger>
              <SelectContent>
                {relationTypes.map((type) => (
                  <SelectItem
                    key={type.value}
                    value={type.value}
                    className={referent?.relation_type === type.value ? 'font-medium' : ''}
                    style={referent?.relation_type === type.value ? { 
                      backgroundColor: colors.primaryWithOpacity(0.1) 
                    } : {}}
                  >
                    {type.label}
                    {referent?.relation_type === type.value && ' (attendu)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {localFormData.relationTypeConfirmed && localFormData.relationTypeConfirmed !== referent?.relation_type && (
              <p className="text-amber-600 text-sm font-['Bricolage_Grotesque']">
                ⚠️ Attention : Votre relation diffère de celle attendue par le recruteur.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
